// Vorprüfung der E-Rechnung – reine Funktion (SPEZIFIKATION §4.1, §4.15; AP3).
//
//   preflight(input) → { required, reason, reasonText, findings, blocking }
//
// 1. Muss eine XML entstehen? (§4.1) – Pflicht (Stichtag, spätestes Umsatzdatum, Geschäftskunde im Inland, über der
//    Kleinbetragsgrenze, kein Kleinunternehmer), Wunsch des Kunden, öffentliche Stelle mit Leitweg-ID. Storno und
//    Korrektur folgen ihrem Bezugsbeleg, wenn der eine XML hat (`linked`).
// 2. Befunde P-01 … P-20 (§4.15) mit Feld und Korrekturweg (`fix` = Route zum Stammdatensatz). Ein Befund „error“
//    verhindert das Festschreiben – aber nur, wenn eine XML entstehen muss; sonst prüft die Vorprüfung den Inhalt gar
//    nicht und meldet höchstens Hinweise (P-07).
//
// Dieselbe Funktion dient Entwürfen (Beleg-Editor, heutige Stammdaten), festgeschriebenen Belegen (Schnappschüsse)
// und der Vorschau des Rechnungslaufs ohne Beleg (`einvoice.outbound.preflightDraft`). Die Abbildung (lib/map)
// prüft beim Erzeugen noch einmal und zusätzlich die Summenregeln – die Codes sind dieselben.
import {
  creditorIdProblem,
  einvoiceAddressProblem,
  isValidEInvoiceEmail,
  isValidIban,
  leitwegIdProblem,
  mandateReferenceProblem,
} from '@shared/einvoice'
import { formatDate, formatEuro } from '@shared/format'
import type { Cents } from '@shared/money'
import type { Qty } from '@shared/qty'
import type { IsoDate } from '@shared/time'
import { isCode } from '../codelists'
import { cannotBuildMessage } from '../model/sentences'
import type { EInvoicePartnerSnapshot, EInvoicePurpose, EInvoiceTenantSnapshot } from '../map/source'
import { brand } from '@shared/brand'

export type FindingLevel = 'error' | 'warning' | 'info'

export interface PreflightFinding {
  code: string
  level: FindingLevel
  /** ein fertiger deutscher Satz ohne Werte der Parteien (er wird mit dem Prüfergebnis abgelegt) */
  message: string
  /** BT/BG oder Feldpfad, z. B. 'BT-49', 'lines[3].unitCode' */
  field: string | null
  /** Route zum Datensatz, an dem sich der Befund beheben lässt */
  fix: string | null
}

/**
 * Warum (keine) XML: mandatory = gesetzliche Pflicht · requested = Wunsch des Kunden · public = öffentliche Stelle mit
 * Leitweg-ID · not_required = keine Bedingung erfüllt · disabled = Funktion ausgeschaltet · not_applicable = keine
 * Rechnung bzw. Storno ohne XML des Ursprungs · suppressed = Storno im Ablauf „Rechnung berichtigen“.
 */
export const REQUIREMENT_REASONS = [
  'mandatory',
  'requested',
  'public',
  'not_required',
  'disabled',
  'not_applicable',
  'suppressed',
] as const
export type RequirementReason = (typeof REQUIREMENT_REASONS)[number]
/** Gründe, aus denen eine XML entsteht (einvoice_outbound.reason) */
export type IssueReason = Extract<RequirementReason, 'mandatory' | 'requested' | 'public'>

export interface PreflightResult {
  required: boolean
  reason: RequirementReason
  /** ein Satz für Menschen: „E-Rechnungspflicht: Geschäftskunde im Inland, über 250,00 €, Leistung ab 01.01.2027.“ */
  reasonText: string
  findings: PreflightFinding[]
  /** true: Festschreiben scheitert (eine XML muss entstehen und mindestens ein Befund ist ein Fehler) */
  blocking: boolean
}

export interface PreflightSettings {
  /** Stichtag der Ausstellungspflicht; null = keine Pflicht hinterlegt */
  mandatoryFrom: IsoDate | null
  /** Kleinbetragsrechnung bis einschließlich (brutto) */
  smallAmountCents: Cents
}

export interface PreflightLine {
  pos: number
  /** item | text | fee | discount | deposit | deposit_return | rounding */
  kind: string
  taxCodeId: string | null
  /** UN/ECE-Code der Einheit (BT-130); Abzugszeile einer Rücknahme: der Code der Rücknahmezeile */
  unitCode: string | null
  qty: Qty
  netCents: Cents
  grossCents: Cents
  /** Abzug einer Rücknahme (`customFields.nettedReturn`) */
  nettedReturn?: { number: string | null; unitCode: string | null } | null
}

export interface PreflightTaxCode {
  taxCodeId: string
  label: string
  /** S, Z, E, AE, K, G, O */
  category: string
  rateBp: number
  exemptionReason: string | null
  exemptionCode: string | null
}

export interface PreflightInput {
  /** Funktionsschalter `einvoice` des Mandanten */
  enabled: boolean
  settings: PreflightSettings
  purpose: EInvoicePurpose
  /** Storno/Korrektur: Hat der Bezugsbeleg eine XML (und keine Vormerkung)? Dann entsteht immer eine – mit seinem Grund */
  linked?: { hasXml: boolean; reason: IssueReason | null; suppressed: boolean } | null
  document: {
    number: string | null
    documentDate: IsoDate
    serviceDate: IsoDate | null
    serviceDateTo: IsoDate | null
    priceMode: 'net' | 'gross'
    currency: string
    /** Bruttobetrag des Belegs mit Vorzeichen, wie der Beleg ihn trägt (Storno negativ) */
    grossCents: Cents
    externalRef: string | null
    buyerReference: string | null
    shippingCountry: string | null
  }
  tenant: EInvoiceTenantSnapshot
  partner: EInvoicePartnerSnapshot | null
  /** Kennung des Kunden für den Korrekturweg */
  partnerId: string | null
  /** Kunde verlangt seine Bestellnummer (partner.requires_order_ref – heutiger Stamm) */
  requiresOrderRef: boolean
  lines: readonly PreflightLine[]
  taxes: readonly PreflightTaxCode[]
  /** Leistungsdaten aller Lieferscheine des Belegs (Storno/Korrektur: die der Bezugsrechnungen) */
  serviceDates: readonly IsoDate[]
  /** BG-3: Bezugsbelege mit Nummer und Datum */
  references: readonly { number: string | null; documentDate: IsoDate | null }[]
  /** beim Festschreiben schon bezahlt bzw. verrechnet: an der Tür (bar, Karte, Leergut) und Guthaben (Rechnung berichtigen), Cent */
  doorPaidCents: Cents
  /**
   * Nachträgliche Ausgabe auf Wunsch (`einvoice.outbound.issue`): Auch ohne Pflicht, Wunsch oder Leitweg-ID entsteht
   * eine XML (Grund „Wunsch des Kunden“) – außer die Funktion ist aus oder das Storno ist vorgemerkt.
   */
  force?: boolean
}

// ---- Korrekturwege (Routen der Oberfläche) ------------------------------------------------------------------------

export const FIX_ROUTES = {
  company: '/settings/company',
  einvoiceSettings: '/settings/einvoice',
  taxes: '/settings/masterdata/taxes',
  units: '/settings/masterdata/units',
  customer: (partnerId: string | null): string | null => (partnerId ? `/sales/customers/${partnerId}` : null),
} as const

const REVERSALS: ReadonlySet<EInvoicePurpose> = new Set(['invoice_reversal', 'credit_note_reversal'])
const SETTLEMENT: ReadonlySet<EInvoicePurpose> = new Set(['credit_note', 'invoice_reversal'])
const WISH_FORMATS: ReadonlySet<string> = new Set(['xrechnung', 'zugferd'])

const text = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}
const maxDate = (dates: readonly (IsoDate | null | undefined)[]): IsoDate | null =>
  dates.reduce<IsoDate | null>((max, date) => (date && (!max || date > max) ? date : max), null)
const minDate = (dates: readonly (IsoDate | null | undefined)[]): IsoDate | null =>
  dates.reduce<IsoDate | null>((min, date) => (date && (!min || date < min) ? date : min), null)

/** Spätestes Umsatzdatum (§4.1 Nr. 1): Leistungsdatum bzw. -ende des Belegs und alle Liefertage. */
export function latestServiceDate(input: Pick<PreflightInput, 'document' | 'serviceDates'>): IsoDate {
  const own = input.document.serviceDateTo ?? input.document.serviceDate ?? input.document.documentDate
  return maxDate([own, ...input.serviceDates]) ?? input.document.documentDate
}

/** Frühestes Umsatzdatum – für den Hinweis P-20 (Sammelrechnung über den Stichtag). */
export function earliestServiceDate(input: Pick<PreflightInput, 'document' | 'serviceDates'>): IsoDate {
  const own = input.document.serviceDate ?? input.document.serviceDateTo ?? input.document.documentDate
  return minDate([own, ...input.serviceDates]) ?? input.document.documentDate
}

const isLeitwegId = (value: string | null): boolean => value !== null && leitwegIdProblem(value) === null

// ---- 1. Muss eine XML entstehen? ------------------------------------------------------------------------------------

export interface Requirement {
  required: boolean
  reason: RequirementReason
  reasonText: string
}

export function requirementOf(input: PreflightInput): Requirement {
  if (!input.enabled) {
    return {
      required: false,
      reason: 'disabled',
      reasonText: 'Die Funktion E-Rechnung ist ausgeschaltet – es entsteht keine XML.',
    }
  }
  // Storno: genau dann eine XML, wenn der Ursprung eine hat (außer der Vormerkung „Rechnung berichtigen“)
  if (REVERSALS.has(input.purpose)) {
    if (input.linked?.suppressed) {
      return {
        required: false,
        reason: 'suppressed',
        reasonText:
          'Storno im Ablauf „Rechnung berichtigen“ – der Kunde erhält nur die berichtigte Rechnung, das Storno bekommt keine XML.',
      }
    }
    if (input.linked?.hasXml) {
      return {
        required: true,
        reason: input.linked.reason ?? 'requested',
        reasonText: 'Der stornierte Beleg wurde als E-Rechnung ausgestellt – das Storno bekommt ebenfalls eine.',
      }
    }
    return {
      required: false,
      reason: 'not_applicable',
      reasonText: 'Der stornierte Beleg hat keine E-Rechnung – das Storno bekommt keine XML.',
    }
  }

  const partner = input.partner
  if (!partner)
    return { required: false, reason: 'not_applicable', reasonText: 'Ohne Kunden am Beleg entsteht keine E-Rechnung.' }
  const type = partner.type ?? 'business'
  const format = text(partner.einvoiceFormat)
  const wish = format !== null && WISH_FORMATS.has(format)
  const country = (text(partner.address?.country) ?? 'DE').toUpperCase()
  const gross = Math.abs(input.document.grossCents)
  const latest = latestServiceDate(input)
  const from = input.settings.mandatoryFrom

  const mandatory =
    type === 'business' &&
    from !== null &&
    latest >= from &&
    !input.tenant.isSmallBusiness &&
    country === 'DE' &&
    gross > input.settings.smallAmountCents
  if (mandatory) {
    return {
      required: true,
      reason: 'mandatory',
      reasonText: `E-Rechnungspflicht: Geschäftskunde im Inland, Rechnungsbetrag über ${formatEuro(input.settings.smallAmountCents)}, Leistung ab ${formatDate(from)}.`,
    }
  }
  if (type === 'public') {
    if (text(input.document.buyerReference)) {
      return {
        required: true,
        reason: 'public',
        reasonText: 'Öffentliche Stelle mit Leitweg-ID – die Rechnung wird als E-Rechnung ausgestellt.',
      }
    }
    if (wish)
      return { required: true, reason: 'requested', reasonText: 'Die öffentliche Stelle verlangt E-Rechnungen.' }
    return {
      required: false,
      reason: 'not_required',
      reasonText: 'Öffentliche Stelle ohne Leitweg-ID und ohne E-Rechnungswunsch – es entsteht keine XML.',
    }
  }
  if (wish && type !== 'private') {
    return {
      required: true,
      reason: 'requested',
      reasonText: 'Der Kunde hat E-Rechnung als Rechnungsformat hinterlegt.',
    }
  }
  // Korrektur/berichtigte Rechnung: folgt der Bezugsrechnung, wenn die als E-Rechnung ausgestellt wurde
  if (input.linked?.hasXml) {
    return {
      required: true,
      reason: input.linked.reason ?? 'requested',
      reasonText: 'Die Bezugsrechnung wurde als E-Rechnung ausgestellt – dieser Beleg bekommt ebenfalls eine.',
    }
  }
  if (input.tenant.isSmallBusiness && type === 'business') {
    return {
      required: false,
      reason: 'not_required',
      reasonText: 'Kleinunternehmer: keine Pflicht zur E-Rechnung (§ 34a UStDV) – XML nur auf Wunsch des Kunden.',
    }
  }
  if (type === 'private')
    return { required: false, reason: 'not_required', reasonText: 'Privatkunde – es entsteht keine XML.' }
  if (from === null) {
    return {
      required: false,
      reason: 'not_required',
      reasonText: 'Kein Stichtag der E-Rechnungspflicht hinterlegt und kein Wunsch des Kunden.',
    }
  }
  if (latest < from) {
    return {
      required: false,
      reason: 'not_required',
      reasonText: `Die Leistung liegt vor dem Stichtag der E-Rechnungspflicht (${formatDate(from)}).`,
    }
  }
  if (country !== 'DE')
    return {
      required: false,
      reason: 'not_required',
      reasonText: 'Kunde ohne Rechnungsanschrift im Inland – keine Pflicht zur E-Rechnung.',
    }
  return {
    required: false,
    reason: 'not_required',
    reasonText: `Kleinbetragsrechnung bis ${formatEuro(input.settings.smallAmountCents)} – keine Pflicht zur E-Rechnung.`,
  }
}

// ---- 2. Befunde --------------------------------------------------------------------------------------------------------

export function preflight(input: PreflightInput): PreflightResult {
  const base = requirementOf(input)
  const forced =
    input.force === true &&
    !base.required &&
    base.reason !== 'disabled' &&
    base.reason !== 'suppressed' &&
    input.partner !== null
  const requirement: Requirement = forced
    ? { required: true, reason: 'requested', reasonText: 'Die E-Rechnung wird nachträglich auf Wunsch ausgestellt.' }
    : base
  const findings: PreflightFinding[] = []
  const add = (level: FindingLevel, code: string, message: string, field: string | null, fix: string | null): void => {
    findings.push({ code, level, message, field, fix })
  }
  const partner = input.partner
  const customerFix = FIX_ROUTES.customer(input.partnerId)

  // Hinweise, die auch ohne XML gelten
  if (!requirement.required && partner?.type === 'public' && requirement.reason === 'not_required') {
    add(
      'info',
      'P-07',
      'Keine E-Rechnung erzeugt – Leitweg-ID fehlt (Pflichten nach ERechV bzw. Landesrecht prüfen).',
      'BT-10',
      customerFix,
    )
  }
  if (!requirement.required) return { ...requirement, findings, blocking: false }

  const tenant = input.tenant
  const doc = input.document
  const settlement = SETTLEMENT.has(input.purpose) || input.purpose === 'credit_note_reversal'
  const categories = new Set(input.taxes.map((tax) => tax.category))
  const onlyO = categories.size === 1 && categories.has('O')

  // ---- Betrieb ----
  const vatId = text(tenant.vatId)
  const taxNumber = text(tenant.taxNumber)
  if (!onlyO && !vatId && !taxNumber) {
    add(
      'error',
      'P-01',
      'Der Betrieb braucht eine USt-IdNr. oder eine Steuernummer (BR-DE-16).',
      'BT-31',
      FIX_ROUTES.company,
    )
  }
  const sellerIds = [text(tenant.contact?.gln), text(partner?.supplierRefAtCustomer)].filter(Boolean)
  if (!(onlyO ? null : vatId) && !text(tenant.registerNumber) && sellerIds.length === 0) {
    add(
      'error',
      'P-01b',
      onlyO
        ? 'Bei nicht steuerbaren Rechnungen entfällt die USt-IdNr.; der Betrieb braucht eine Registernummer, eine eigene GLN oder die Lieferantennummer beim Kunden (BR-CO-26).'
        : 'Der Betrieb braucht eine USt-IdNr., eine Registernummer, eine eigene GLN oder die Lieferantennummer beim Kunden (BR-CO-26).',
      'BT-29',
      FIX_ROUTES.company,
    )
  }
  const address = tenant.address ?? {}
  if (!text(tenant.legalName) && !text(tenant.name))
    add('error', 'P-02', 'Es fehlt der Name des Betriebs.', 'BT-27', FIX_ROUTES.company)
  if (!text(address.street) || !text(address.zip) || !text(address.city) || !text(address.country)) {
    add(
      'error',
      'P-02',
      'In den Angaben zum Betrieb fehlt die vollständige Anschrift (Straße, PLZ, Ort, Land).',
      'BG-5',
      FIX_ROUTES.company,
    )
  } else if (!isCode('country', (address.country as string).trim().toUpperCase())) {
    add(
      'error',
      'P-02',
      'Das Land in der Anschrift des Betriebs ist kein Ländercode nach ISO 3166-1 (z. B. DE).',
      'BT-40',
      FIX_ROUTES.company,
    )
  }
  const sellerEndpoint = text(tenant.einvoiceAddress)
  if (!sellerEndpoint) {
    add(
      'error',
      'P-03',
      'Es fehlt die E-Rechnungs-Adresse des Betriebs (E-Mail-Adresse oder z. B. 0088:<GLN>).',
      'BT-34',
      FIX_ROUTES.company,
    )
  } else {
    const problem = einvoiceAddressProblem(sellerEndpoint)
    if (problem) add('error', 'P-03', `E-Rechnungs-Adresse des Betriebs: ${problem}`, 'BT-34', FIX_ROUTES.company)
  }
  const contact = tenant.contact ?? {}
  if (!text(contact.name))
    add(
      'error',
      'P-04',
      'Es fehlt die Kontaktstelle für Rechnungen (z. B. „Buchhaltung“).',
      'BT-41',
      FIX_ROUTES.company,
    )
  if ((text(contact.phone)?.match(/\d/g)?.length ?? 0) < 3) {
    add(
      'error',
      'P-04',
      'Es fehlt die Telefonnummer des Betriebs (mindestens drei Ziffern).',
      'BT-42',
      FIX_ROUTES.company,
    )
  }
  const email = text(contact.email)
  if (!email || !isValidEInvoiceEmail(email)) {
    add('error', 'P-04', 'Es fehlt eine gültige E-Mail-Adresse des Betriebs.', 'BT-43', FIX_ROUTES.company)
  }

  // ---- Kunde ----
  if (!partner) {
    add('error', 'P-05', 'Der Beleg hat keinen Kunden.', 'BG-7', null)
  } else {
    if (!text(partner.name)) add('error', 'P-05', 'Es fehlt der Name des Kunden.', 'BT-44', customerFix)
    const billing = partner.address
    if (!billing || !text(billing.street) || !text(billing.zip) || !text(billing.city)) {
      add(
        'error',
        'P-05',
        'Für den Kunden fehlt die vollständige Rechnungsanschrift (Straße, PLZ, Ort, Land).',
        'BG-8',
        customerFix,
      )
    } else if (!isCode('country', (text(billing.country) ?? 'DE').toUpperCase())) {
      add(
        'error',
        'P-05',
        'Das Land der Rechnungsanschrift ist kein Ländercode nach ISO 3166-1 (z. B. DE).',
        'BT-55',
        customerFix,
      )
    }
    const endpoint = text(partner.einvoiceAddress) ?? text(partner.email)
    if (!endpoint) {
      add(
        'error',
        'P-06',
        'Es fehlt die E-Mail-Adresse für Rechnungen des Kunden (oder eine andere E-Rechnungs-Adresse).',
        'BT-49',
        customerFix,
      )
    } else {
      const problem = einvoiceAddressProblem(endpoint)
      if (problem) add('error', 'P-06', `E-Rechnungs-Adresse des Kunden: ${problem}`, 'BT-49', customerFix)
    }
    if (partner.type === 'public') {
      const reference = text(doc.buyerReference)
      if (!reference) {
        add('error', 'P-07', 'Für die öffentliche Stelle fehlt die Leitweg-ID (Käuferreferenz).', 'BT-10', customerFix)
      } else if (!isLeitwegId(reference)) {
        add(
          'error',
          'P-07',
          `Die Käuferreferenz ist keine gültige Leitweg-ID: ${leitwegIdProblem(reference) ?? ''}`.trim(),
          'BT-10',
          customerFix,
        )
      }
    }
    if (partner.type === 'business' && text(partner.einvoiceFormat) === 'pdf' && requirement.reason === 'mandatory') {
      add(
        'info',
        'P-17',
        'Der Kunde wünscht PDF – wegen der gesetzlichen Pflicht wird die E-Rechnung trotzdem erzeugt.',
        'BT-49',
        customerFix,
      )
    }
  }
  if (input.requiresOrderRef && !text(doc.externalRef) && !settlement) {
    add(
      'error',
      'P-08',
      'Der Kunde verlangt seine Bestellnummer auf der Rechnung – bitte tragen Sie sie am Beleg ein.',
      'BT-13',
      null,
    )
  }
  if (doc.currency !== 'EUR') add('error', 'P-09', `E-Rechnungen stellt ${brand.name} nur in Euro aus.`, 'BT-5', null)

  // ---- Positionen und Steuer ----
  const taxById = new Map(input.taxes.map((tax) => [tax.taxCodeId, tax]))
  for (const line of input.lines) {
    if (line.kind === 'text') continue
    if (line.kind === 'rounding') {
      add(
        'error',
        'P-13',
        `Position ${line.pos}: Eine Rundungszeile gehört nicht auf eine Rechnung.`,
        `lines[${line.pos}]`,
        null,
      )
      continue
    }
    const amount = doc.priceMode === 'gross' ? line.grossCents : line.netCents
    if (!line.taxCodeId || !taxById.has(line.taxCodeId)) {
      if (amount !== 0 || line.kind === 'discount') {
        add(
          'error',
          line.kind === 'discount' ? 'BR-32' : 'P-10',
          `Position ${line.pos}: Es fehlt der Steuerschlüssel (auch Rabatt- und Gutscheinzeilen brauchen einen).`,
          `lines[${line.pos}].taxCodeId`,
          null,
        )
      }
    }
    // Einheit (BT-130): Pfand, Leergut und Gebühren ohne eigenen Code tragen C62; Belegrabatte keine Einheit
    const netted = line.nettedReturn ?? null
    if (line.kind === 'discount' && !netted) continue
    const code =
      line.kind === 'discount'
        ? text(netted?.unitCode)
        : line.kind === 'deposit' || line.kind === 'deposit_return' || line.kind === 'fee'
          ? (text(line.unitCode) ?? 'C62')
          : text(line.unitCode)
    if (!code || !isCode('unit', code)) {
      add(
        'error',
        'P-11',
        line.kind === 'discount'
          ? `Position ${line.pos}: Der Rücknahmezeile fehlt ein gültiger UN/ECE-Code der Einheit.`
          : `Position ${line.pos}: Der Einheit fehlt ein gültiger UN/ECE-Code (z. B. XCR für Kasten, H87 für Stück).`,
        `lines[${line.pos}].unitCode`,
        FIX_ROUTES.units,
      )
    }
  }
  if (categories.has('O') && categories.size > 1) {
    add(
      'error',
      'P-10',
      'Nicht steuerbare Positionen (z. B. Kaution) dürfen nicht mit anderen Positionen auf einer Rechnung stehen – bitte getrennt berechnen.',
      'BT-118',
      null,
    )
  }
  if (tenant.isSmallBusiness && [...categories].some((category) => category !== 'E')) {
    add(
      'error',
      'P-10',
      'Als Kleinunternehmer weist der Betrieb keine Umsatzsteuer aus – alle Positionen brauchen einen steuerfreien Steuerschlüssel (Kategorie E).',
      'BT-118',
      FIX_ROUTES.taxes,
    )
  }
  const exempt = input.taxes.filter((tax) => tax.category === 'E')
  if (exempt.length > 0 && !tenant.isSmallBusiness) {
    const reasons = new Set(exempt.map((tax) => text(tax.exemptionReason)).filter(Boolean))
    const codes = new Set(exempt.map((tax) => text(tax.exemptionCode)?.toUpperCase()).filter(Boolean))
    if (reasons.size > 1 || codes.size > 1) {
      add(
        'error',
        'P-10',
        'Steuerfreie Positionen mit verschiedenen Befreiungsgründen bitte getrennt berechnen.',
        'BT-120',
        FIX_ROUTES.taxes,
      )
    } else if (reasons.size === 0 && codes.size === 0) {
      add(
        'error',
        'BR-E-10',
        'Steuerfreie Positionen brauchen einen Befreiungsgrund am Steuerschlüssel.',
        'BT-120',
        FIX_ROUTES.taxes,
      )
    }
  }
  if (categories.has('K')) {
    if (!text(partner?.vatId)) {
      add(
        'error',
        'P-10',
        'Für eine steuerfreie innergemeinschaftliche Lieferung braucht der Kunde eine USt-IdNr. (BR-IC-02).',
        'BT-48',
        customerFix,
      )
    }
    if (!text(doc.shippingCountry) && !text(partner?.address?.country)) {
      add(
        'error',
        'P-10',
        'Für eine innergemeinschaftliche Lieferung fehlt das Land der Lieferanschrift (BR-IC-12).',
        'BT-80',
        customerFix,
      )
    }
  }
  if ((categories.has('K') || categories.has('G')) && !vatId) {
    add(
      'error',
      'P-10',
      'Für innergemeinschaftliche Lieferungen und Ausfuhren braucht der Betrieb eine USt-IdNr. (BR-IC-02, BR-G-02).',
      'BT-31',
      FIX_ROUTES.company,
    )
  }
  if (categories.has('AE') && !text(partner?.vatId) && !text(partner?.registerNumber)) {
    add(
      'error',
      'P-10',
      'Bei Steuerschuldnerschaft des Leistungsempfängers braucht der Kunde eine USt-IdNr. oder Registernummer (BR-AE-02).',
      'BT-48',
      customerFix,
    )
  }
  const seen = new Map<string, PreflightTaxCode>()
  for (const tax of input.taxes) {
    const key = `${tax.category}|${tax.category === 'O' ? '-' : tax.rateBp}`
    const other = seen.get(key)
    if (other) {
      add(
        'warning',
        'P-15',
        `Die Steuerschlüssel „${other.label}“ und „${tax.label}“ haben dieselbe Kategorie und denselben Satz und werden in der E-Rechnung zusammengefasst.`,
        'BG-23',
        FIX_ROUTES.taxes,
      )
    } else seen.set(key, tax)
  }
  if (doc.priceMode === 'gross') {
    add(
      'info',
      'P-16',
      'Brutto-Beleg: Die Steuer ist je Steuersatz aus dem Bruttobetrag gerechnet (zulässig).',
      'BT-117',
      null,
    )
  }

  // ---- Summe, Zahlung ----
  const mirrored = REVERSALS.has(input.purpose) ? -doc.grossCents : doc.grossCents
  if (settlement || input.purpose === 'credit_note_reversal') {
    if (input.purpose === 'credit_note_reversal' ? mirrored < 0 : mirrored <= 0) {
      add(
        'error',
        'P-13',
        input.purpose === 'credit_note_reversal'
          ? 'Das Storno einer Rechnungskorrektur braucht einen Betrag ab null.'
          : 'Eine Rechnungskorrektur braucht einen Betrag über null.',
        'BT-112',
        null,
      )
    }
  } else if (mirrored < 0) {
    add('error', 'P-13', 'Eine Rechnung mit negativer Summe wird als Rechnungskorrektur ausgestellt.', 'BT-112', null)
  }
  const payable = Math.max(0, mirrored - (settlement ? 0 : input.doorPaidCents))
  if (!settlement && payable > 0 && partner) {
    const method = text(partner.paymentMethod) ?? 'transfer'
    if (method === 'direct_debit') {
      const mandate = partner.sepaMandate ?? null
      if (!mandate || !text(mandate.reference) || mandateReferenceProblem(mandate.reference) !== null) {
        add('error', 'P-12', 'Für die Lastschrift fehlt die Mandatsreferenz des Kunden.', 'BT-89', customerFix)
      }
      if (!mandate || !text(mandate.iban) || !isValidIban(mandate.iban)) {
        add('error', 'P-12', 'Für die Lastschrift fehlt eine gültige IBAN des Kunden.', 'BT-91', customerFix)
      }
      const creditorId = text(tenant.bank?.creditor_id)
      if (!creditorId) {
        add('error', 'P-12', 'Für die Lastschrift fehlt die Gläubiger-ID des Betriebs.', 'BT-90', FIX_ROUTES.company)
      } else {
        const problem = creditorIdProblem(creditorId)
        if (problem) add('error', 'P-12', `Gläubiger-ID des Betriebs: ${problem}`, 'BT-90', FIX_ROUTES.company)
      }
    } else if (method !== 'cash') {
      const iban = text(tenant.bank?.iban)
      if (!iban || !isValidIban(iban)) {
        add('error', 'P-12', 'Für die Überweisung fehlt eine gültige IBAN des Betriebs.', 'BT-84', FIX_ROUTES.company)
      }
    }
  }
  if (!settlement && input.doorPaidCents > Math.max(0, mirrored)) {
    add(
      'info',
      'P-19',
      `An der Tür wurde mehr bezahlt (${formatEuro(input.doorPaidCents)}) als die Rechnung ausweist – als bezahlt gilt der Rechnungsbetrag.`,
      'BT-113',
      null,
    )
  }

  // ---- Leistungsdatum, Bezug, Stichtag ----
  if (!doc.serviceDate && !doc.serviceDateTo && input.serviceDates.length === 0) {
    add('error', 'P-14', 'Es fehlt das Leistungsdatum.', 'BT-72', null)
  }
  if (input.purpose !== 'invoice' && input.purpose !== 'receipt_invoice') {
    const complete = input.references.filter((reference) => text(reference.number) && reference.documentDate)
    if (complete.length === 0 || complete.length < input.references.length) {
      add('error', 'P-18', 'Der Bezugsbeleg braucht Nummer und Datum (BG-3).', 'BG-3', null)
    }
  }
  if (
    requirement.reason === 'mandatory' &&
    input.settings.mandatoryFrom &&
    earliestServiceDate(input) < input.settings.mandatoryFrom
  ) {
    add(
      'info',
      'P-20',
      `E-Rechnungspflicht für die ganze Rechnung, weil Lieferungen ab dem ${formatDate(input.settings.mandatoryFrom)} enthalten sind.`,
      'BG-14',
      null,
    )
  }

  const blocking = findings.some((finding) => finding.level === 'error')
  return { ...requirement, findings, blocking }
}

/**
 * Fehler der Vorprüfung als Sätze für `businessRule(…)` – Festschreiben scheitert, nichts wird gebucht. Ohne Prüfcodes
 * (die stehen in `details.findings`): „Rechnung: Die E-Rechnung lässt sich nicht erzeugen. Es fehlt die E-Mail-Adresse …“
 */
export function preflightMessage(
  findings: readonly Pick<PreflightFinding, 'code' | 'level' | 'message'>[],
  subject: string,
): string {
  return `${subject}: ${cannotBuildMessage(findings)}`
}

/**
 * „Bereit für E-Rechnungen?“ (§5.5): die Angaben zum Betrieb, die jede E-Rechnung braucht (P-01 … P-04) – als Fehler –,
 * und der Hinweis auf P-01b, wenn weder USt-IdNr. noch Registernummer noch eigene GLN hinterlegt sind (dann braucht
 * jeder Kunde die Lieferantennummer beim Kunden).
 */
export function readinessFindings(tenant: EInvoiceTenantSnapshot): PreflightFinding[] {
  const probe = preflight({
    enabled: true,
    settings: { mandatoryFrom: null, smallAmountCents: 0 },
    purpose: 'invoice',
    document: {
      number: null,
      documentDate: '2000-01-01',
      serviceDate: '2000-01-01',
      serviceDateTo: null,
      priceMode: 'net',
      currency: 'EUR',
      grossCents: 0,
      externalRef: null,
      buyerReference: null,
      shippingCountry: null,
    },
    tenant,
    // ein vollständiger Probe-Kunde: Es zählen nur die Befunde zum Betrieb
    partner: {
      number: null,
      name: 'Probe',
      name2: null,
      type: 'business',
      vatId: null,
      taxNumber: null,
      email: 'probe@example.org',
      address: { name: null, street: 'Probe 1', zip: '00000', city: 'Probe', country: 'DE', note: null },
      einvoiceFormat: 'xrechnung',
      paymentMethod: 'cash',
    },
    partnerId: null,
    requiresOrderRef: false,
    lines: [],
    taxes: [],
    serviceDates: [],
    references: [],
    doorPaidCents: 0,
  })
  const findings = probe.findings.filter((finding) => ['P-01', 'P-02', 'P-03', 'P-04'].includes(finding.code))
  if (probe.findings.some((finding) => finding.code === 'P-01b')) {
    findings.push({
      code: 'P-01b',
      level: 'warning',
      message:
        'Ohne USt-IdNr., Registernummer und eigene GLN braucht jede E-Rechnung die Lieferantennummer beim Kunden (BR-CO-26) – hinterlegen Sie am besten die eigene GLN.',
      field: 'BT-29',
      fix: FIX_ROUTES.company,
    })
  }
  return findings
}
